import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Loader from "./Loader";

const LoadingContext = createContext({
  isLoading: false,
  startLoading: () => {},
  stopLoading: () => {},
});

let pendingRequests = 0;
const listeners = new Set();

function notifyListeners() {
  listeners.forEach((listener) => listener(pendingRequests));
}

function bump(delta) {
  pendingRequests = Math.max(0, pendingRequests + delta);
  notifyListeners();
}

let interceptorsReady = false;

function setupAxiosInterceptors() {
  if (interceptorsReady) return;
  interceptorsReady = true;

  axios.interceptors.request.use(
    (config) => {
      if (!config?.skipGlobalLoader) {
        bump(1);
      }
      return config;
    },
    (error) => {
      if (!error?.config?.skipGlobalLoader) {
        bump(-1);
      }
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => {
      if (!response?.config?.skipGlobalLoader) {
        bump(-1);
      }
      return response;
    },
    (error) => {
      if (!error?.config?.skipGlobalLoader) {
        bump(-1);
      }
      return Promise.reject(error);
    },
  );
}

setupAxiosInterceptors();

export function LoadingProvider({ children }) {
  const [pending, setPending] = useState(pendingRequests);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const listener = (count) => setPending(count);
    listeners.add(listener);
    setPending(pendingRequests);
    return () => listeners.delete(listener);
  }, []);

  useEffect(() => {
    if (pending > 0) {
      const timer = setTimeout(() => setVisible(true), 120);
      return () => clearTimeout(timer);
    }
    setVisible(false);
  }, [pending]);

  const startLoading = useCallback(() => bump(1), []);
  const stopLoading = useCallback(() => bump(-1), []);

  const value = useMemo(
    () => ({
      isLoading: pending > 0,
      startLoading,
      stopLoading,
    }),
    [pending, startLoading, stopLoading],
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {visible && (
        <div className="global-loader-overlay" aria-busy="true" aria-live="polite">
          <Loader />
        </div>
      )}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  return useContext(LoadingContext);
}
